/**
 * QuickAccessBar — the ribbon's top strip (Faz 3.5): New / Open / Save /
 * SaveAs / Plot + Undo / Redo with HISTORY dropdowns (jump back N steps by
 * running undo N times). Sits INSIDE the ribbon, under the suite topbar
 * (the @suite/topbar slot is untouched — risk §6 mitigation).
 */

import { Box, Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Tooltip } from '@chakra-ui/react';
import { ChevronDown, FilePlus, FileText, FolderOpen, Printer, Redo2, Save, Undo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../../state/store';
import { useCommandManager } from '../../../state/commandStore';

/** Friendly label for a history action type. */
function historyLabel(type: string): string {
    const names: Record<string, string> = {
        'cmd.add': 'Add',
        'cmd.delete': 'Delete',
        'cmd.modify': 'Modify',
        'cmd.replace': 'Replace',
        'cmd.patch': 'Patch',
        // Faz 10.3: mark/group entries ride the same stack.
        'history.mark': 'Mark',
        'history.group': 'Group',
        'bim.add': 'Add Element',
        'bim.modify': 'Modify Element',
        'bim.delete': 'Delete Element',
    };
    return names[type] ?? type;
}

function HistoryDropdown({ direction }: { direction: 'undo' | 'redo' }) {
    const { t } = useTranslation();
    const history = useAppStore((s) => s.history);
    const historyIndex = useAppStore((s) => s.historyIndex);

    // Undo list: entries from the current index backwards (most recent
    // first), capped at 15 (OCS UNDO_HISTORY depth).
    const undoEntries = history.slice(0, historyIndex + 1).reverse().slice(0, 15);
    const redoEntries = history.slice(historyIndex + 1).slice(0, 15);

    const entries = direction === 'undo' ? undoEntries : redoEntries;
    const canStep = direction === 'undo' ? historyIndex >= 0 : historyIndex < history.length - 1;

    const jump = (steps: number) => {
        const app = useAppStore.getState();
        for (let i = 0; i < steps; i++) app[direction]();
    };

    return (
        <Menu closeOnSelect autoSelect={false} placement="bottom-start">
            <MenuButton
                as={IconButton}
                size="xs"
                variant="ghost"
                aria-label={t(direction === 'undo' ? 'ribbon.qat.undoHistory' : 'ribbon.qat.redoHistory')}
                icon={<Icon as={ChevronDown} boxSize="10px" color="text.secondary" />}
                isDisabled={!canStep}
                minWidth="16px"
                height="20px"
                px="2px"
            />
            <MenuList w="max-content" minW="180px" bg="bg.panel" borderColor="border.base">
                {(entries.length === 0 || !canStep) && (
                    <MenuItem isDisabled fontSize="12px" color="text.secondary">
                        {t('ribbon.qat.historyEmpty')}
                    </MenuItem>
                )}
                {canStep && entries.map((entry, i) => (
                    <MenuItem
                        key={`${entry.timestamp}-${i}`}
                        fontSize="12px"
                        color="text.primary"
                        onClick={() => jump(direction === 'undo' ? i + 1 : entries.length - i)}
                    >
                        {/* Named steps (Faz 10.3 labels, OCS
                            history_dropdown_labels) win over the type name. */}
                        {`${direction === 'undo' ? i + 1 : entries.length - i} ${t('ribbon.qat.steps')} — ${entry.label ?? historyLabel(entry.type)}`}
                    </MenuItem>
                ))}
            </MenuList>
        </Menu>
    );
}

export function QuickAccessBar() {
    const { t } = useTranslation();
    const execute = useCommandManager((s) => s.executeCommand);
    const undo = useAppStore((s) => s.undo);
    const redo = useAppStore((s) => s.redo);
    const history = useAppStore((s) => s.history);
    const historyIndex = useAppStore((s) => s.historyIndex);

    const buttons = [
        { key: 'qnew', label: t('ribbon.qat.new'), icon: FilePlus, run: () => execute('newProject') },
        { key: 'open', label: t('ribbon.qat.open'), icon: FolderOpen, run: () => execute('openProject') },
        { key: 'save', label: t('ribbon.qat.save'), icon: Save, run: () => execute('saveProject') },
        { key: 'saveAs', label: t('ribbon.qat.saveAs'), icon: FileText, run: () => execute('saveProjectAs') },
        { key: 'plot', label: t('ribbon.qat.plot'), icon: Printer, run: () => execute('cmd.file.plot') },
    ];

    return (
        <Flex h="24px" align="center" px={2} gap={0} flexShrink={0} bg="bg.toolbar" borderBottom="1px solid" borderColor="border.base">
            {buttons.map((b) => (
                <Tooltip key={b.key} label={b.label} placement="bottom" openDelay={400} hasArrow>
                    <IconButton
                        size="xs"
                        variant="ghost"
                        aria-label={b.label}
                        icon={<Icon as={b.icon} boxSize="13px" color="icon.base" />}
                        onClick={b.run}
                        minWidth="22px"
                        height="20px"
                        flexShrink={0}
                    />
                </Tooltip>
            ))}
            <Box w="1px" h="12px" bg="border.base" mx={1} flexShrink={0} aria-hidden="true" />
            <Tooltip label={t('ribbon.qat.undo')} placement="bottom" hasArrow>
                <IconButton
                    size="xs"
                    variant="ghost"
                    aria-label={t('ribbon.qat.undo')}
                    icon={<Icon as={Undo2} boxSize="13px" color="icon.base" />}
                    onClick={undo}
                    isDisabled={historyIndex < 0}
                    minWidth="22px"
                    height="20px"
                    flexShrink={0}
                />
            </Tooltip>
            <HistoryDropdown direction="undo" />
            <Tooltip label={t('ribbon.qat.redo')} placement="bottom" hasArrow>
                <IconButton
                    size="xs"
                    variant="ghost"
                    aria-label={t('ribbon.qat.redo')}
                    icon={<Icon as={Redo2} boxSize="13px" color="icon.base" />}
                    onClick={redo}
                    isDisabled={historyIndex >= history.length - 1}
                    minWidth="22px"
                    height="20px"
                    flexShrink={0}
                />
            </Tooltip>
            <HistoryDropdown direction="redo" />
        </Flex>
    );
}
